<template>
  <div class="flex flex-col">
    <app-header class="pl-4 sticky top-0 z-10">
      <filter-menu :show-channels="true"
                   :show-markets="true"
                   :show-granularity="true"
                   :use-logical-bots="true"
                   :is-loading="loaders.filters"
                   @filter-change="onFilterChanged"
      />
      <export-statistics-menu :bots="botList"
                              :multi-bots="multiBots"
                              :date-from="dateFrom"
                              :date-to="dateTo"
                              class="ml-auto mr-4"
      />
    </app-header>

    <analytics-charts :loaders="loaders"
                      :bot-id="selectedBotId"
                      class="my-12 w-11/12 xl:w-full z-0"
                      @load-assistance-resolution="handleAssistanceResolutions"
    />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader'
import FilterMenu from './filter/FilterMenu'
import AnalyticsCharts from '@/components/analytics/charts/AnalyticsCharts'
import ExportStatisticsMenu from '@/components/analytics/ExportStatisticsMenu'
import analyticsService from '@/services/amio/analytics.service'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import channelModule from '@/store/modules/channel.module'
import botModule from '@/store/modules/bot.module'
import { BOT_TYPES } from '@/utils/bot.constants'
import toaster from '@/utils/toaster'
import cacheModule from '@/store/modules/cache.module'
import analyticsModule from '@/store/modules/analytics.module'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import marketService from '@/services/amio/market.service'
import marketModule from '@/store/modules/market.module'
import AssistanceResolutionsUtils from '@/utils/assistance-resolutions.utils'
import { FILTER_ITEMS, GRANULARITY_CONTACT, GRANULARITY_REQUEST } from '@/utils/filter.constants'
import { getBotIdsForLogicalBots, getLogicalBotId, resolveBotRouteId } from '@/utils/market.utils'

export default {
  name: 'Analytics',

  metaInfo: {
    title: 'Analytics'
  },

  components: {
    AnalyticsCharts,
    AppHeader,
    FilterMenu,
    ExportStatisticsMenu
  },

  props: {
    botId: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      loaders: {
        requestTotal: false,
        automationRate: false,
        intentUsages: false,
        assistanceResolutions: false,
        assistanceResolutionsUpdate: false,
        filters: false
      },
      requestOutcomes: null,
      assistanceResolutionsUtils: null
    }
  },

  computed: {
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    channels: analyticsFilterModule.getChannels,
    markets: analyticsFilterModule.getMarkets,
    logicalBots: analyticsFilterModule.getLogicalBots,
    granularity: analyticsFilterModule.getGranularity,
    requests: analyticsModule.getRequests,
    contacts: analyticsModule.getContacts,
    outcomes: analyticsModule.getOutcomes,
    answers: answerModule.getAnswerList,
    selectedBot: analyticsFilterModule.getBot,

    isEmailBot() {
      return this.selectedBot?.type === BOT_TYPES.EMAIL
    },

    botList() {
      return botModule.getBotList()
    },

    multiBots() {
      return botModule.getBotVariantList()
    },

    selectedBotId() {
      return this.selectedBot?.id || this.botId || botModule.getBotId()
    },

    // logicalBots === null means "the anchor bot only" (default)
    selectedLogicalBotIds() {
      if (this.logicalBots !== null && this.logicalBots !== undefined) return this.logicalBots

      const anchorLogicalBotId = getLogicalBotId(this.selectedBot)
      return anchorLogicalBotId ? [anchorLogicalBotId] : []
    },

    selectedBotIds() {
      return getBotIdsForLogicalBots(this.selectedLogicalBotIds, this.markets)
    }
  },

  methods: {
    getRouteLogicalBotIds() {
      const { bots } = this.$route.query
      if (typeof bots === 'undefined') return null
      if (bots === '') return []
      return bots ? bots.split(',').filter(Boolean) : []
    },

    // Accepts either a real variant bot id or a logical (multi_bot_id or standalone) id.
    resolvePrimaryBotId(botIdOrLogicalId) {
      const botList = botModule.getBotList()
      if (!botIdOrLogicalId) return botModule.getBotId()

      const isStandaloneLogical = botList.some(({ id, multi_bot_id }) => id === botIdOrLogicalId && !multi_bot_id)
      if (isStandaloneLogical) return botIdOrLogicalId

      const isMultiGroupLogical = botList.some(({ multi_bot_id }) => multi_bot_id === botIdOrLogicalId)
      if (isMultiGroupLogical) return resolveBotRouteId(botIdOrLogicalId) || botIdOrLogicalId

      return botModule.resolvePrimaryBot(botIdOrLogicalId)?.id || botIdOrLogicalId
    },

    // API calls
    async retrieveAnalytics() {
      const chartLoaders = ['requestTotal', 'automationRate', 'intentUsages', 'assistanceResolutions']
      chartLoaders.forEach(loader => {
        this.setLoader(loader, true)
      })

      try {
        // API call
        await this.retrieveRequestOutcomes()
        // setters + mappers
        await this.initChartData()
      } catch (e) {
        toaster.error(e)
      }
    },

    async retrieveChannelList(botId) {
      analyticsFilterModule.setChannels(null)
      await this.retrieveFiltersData(botId)
    },

    async retrieveRequestOutcomes() {
      const isAnchorOnly = this.logicalBots === null || this.logicalBots === undefined
      const marketsAreUnrestricted = this.markets === null || this.markets === undefined
      const shouldFallbackToSelectedBot =
        this.selectedBotId && isAnchorOnly && marketsAreUnrestricted

      let botIds = this.selectedBotIds.length ? this.selectedBotIds : []
      if (!botIds.length && shouldFallbackToSelectedBot) {
        botIds = [this.selectedBotId]
      }
      if (!botIds.length) {
        analyticsModule.setOutcomes({})
        this.requestOutcomes = Object.freeze([])
        return
      }

      const params = {
        botIds,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        channels: this.channels
      }
      const { requestOutcomes, outcomes } = await analyticsService.getRequestOutcomes(params)

      analyticsModule.setOutcomes(requestOutcomeMapper.buildOutcomeMap(outcomes))
      this.requestOutcomes = Object.freeze(requestOutcomes)
    },

    async retrieveFiltersData(botIdOrLogicalId) {
      this.setLoader('filters', true)
      await botModule.setBotList()
      const primaryBotId = this.resolvePrimaryBotId(botIdOrLogicalId)
      try {
        await Promise.all([
          answerModule.setAnswerList(await answerService.list(primaryBotId)),
          analyticsFilterModule.setBot(primaryBotId),
          // keep global bot in sync with "anchor" so sidebar navigation uses the right bot
          botModule.syncBotById(primaryBotId),
          marketModule.setMarketList(await marketService.list())
        ])
        const effectiveBotIds = this.selectedBotIds.length ? this.selectedBotIds : [primaryBotId]
        await channelModule.setChannelListForBots(effectiveBotIds)
      } catch (err) {
        toaster.error(err)
      }
      this.setLoader('filters', false)
    },

    // init chart data
    async initChartData() {
      await this.initAggregatedAutomationOutcomes()
      await this.initTotals()
      await this.initIntentUsages()
      await this.initAssistanceResolutions()
    },

    async initAggregatedAutomationOutcomes() {
      const setter = () => {
        const requests = requestOutcomeMapper.mapRequestOutcomes(this.requestOutcomes, this.outcomes)
        analyticsModule.setRequests(requests)

        if (GRANULARITY_REQUEST === this.granularity) {
          const aggregatedAutomationOutcomes = requestOutcomeMapper.mapAutomationRateData(this.requests, this.isEmailBot)
          analyticsModule.setAggregatedAutomationOutcomes(aggregatedAutomationOutcomes)
        } else {
          const contacts = requestOutcomeMapper.mapContactOutcomes(requests)
          analyticsModule.setContacts(contacts)
          const aggregatedAutomationOutcomes = requestOutcomeMapper.mapAutomationRateData(this.contacts, this.isEmailBot)
          analyticsModule.setAggregatedAutomationOutcomes(aggregatedAutomationOutcomes)
        }
      }
      await this.resolveSetter(setter, 'automationRate')
    },

    async initTotals() {
      const setter = () => {
        if (GRANULARITY_CONTACT === this.granularity) {
          const contactTotal = requestOutcomeMapper.calculateContactTotal(this.contacts)
          analyticsModule.setContactTotal(contactTotal)
        }
        const requestTotal = requestOutcomeMapper.calculateRequestTotal(this.requests)
        analyticsModule.setRequestTotal(requestTotal)
      }
      await this.resolveSetter(setter, 'requestTotal')
    },

    async initIntentUsages() {
      const setter = () => {
        const intentUsages = requestOutcomeMapper.mapAnswerUsages(this.answers, this.requests, false, this.isEmailBot)
        analyticsModule.setIntentUsages(intentUsages)
      }
      await this.resolveSetter(setter, 'intentUsages')
    },

    async initAssistanceResolutions() {
      const from = +new Date(this.dateFrom)
      const to = +new Date(this.dateTo)
      const { timeGranularity } = this.assistanceResolutionsUtils.getTimeGranularityParams(from, to)
      await this.setAssistanceResolutions(from, to, timeGranularity, 'assistanceResolutions')
    },

    async setAssistanceResolutions(from, to, timeGranularity, loader) {
      const setter = () => {
        const intervals = requestOutcomeMapper.mapAssistanceResolutionIntervals(
          this.requests,
          from,
          to,
          timeGranularity
        )
        const assistanceResolutions = requestOutcomeMapper.mapAssistanceResolutionsData(
          intervals
        )
        analyticsModule.setAssistanceResolutions(assistanceResolutions)
      }

      await this.resolveSetter(setter, loader)
    },

    // handlers
    async changeBot(bot) {
      if (this.selectedBotId === bot.id) return false

      botModule.setBot(bot)
      await this.retrieveFiltersData(bot.id)
      return true
    },

    async onFilterChanged(filterValues) {
      analyticsFilterModule.setDateFrom(filterValues[FILTER_ITEMS.DATE_FROM])
      analyticsFilterModule.setDateTo(filterValues[FILTER_ITEMS.DATE_TO])
      analyticsFilterModule.setGranularity(filterValues[FILTER_ITEMS.GRANULARITY])
      analyticsFilterModule.setLogicalBots(filterValues[FILTER_ITEMS.BOTS])
      analyticsFilterModule.setMarkets(filterValues[FILTER_ITEMS.MARKETS])
      analyticsFilterModule.setChannels(filterValues[FILTER_ITEMS.CHANNELS])
      const didChangeBot = await this.changeBot(filterValues[FILTER_ITEMS.BOT])
      if (!didChangeBot) {
        const effectiveBotIds = this.selectedBotIds.length ? this.selectedBotIds : [this.selectedBotId]
        await channelModule.setChannelListForBots(effectiveBotIds)
      }
      await this.retrieveAnalytics()
    },

    async handleAssistanceResolutions({ from, to }) {
      const params = this.assistanceResolutionsUtils.getTimeGranularityParams(
        +new Date(from),
        +new Date(to),
        +new Date(this.dateFrom),
        +new Date(this.dateTo)
      )
      if (!params) return

      const { dateFrom, dateTo, timeGranularity } = params
      await this.setAssistanceResolutions(dateFrom, dateTo, timeGranularity, 'assistanceResolutionsUpdate')
    },

    // utils
    async resolveSetter(setter, loader) {
      if (loader === 'assistanceResolutionsUpdate') this.setLoader('assistanceResolutionsUpdate', true)
      // hack, some time for starting loader on chart, before crunching of data starts,
      // otherwise no loader appears on chart and javascript just runs, without any UI change
      // note: .then(() => setter()), nextTick and Promise.resolve don't do a trick
      await this.sleep(100)
      await setter()
      this.setLoader(loader, false)
    },

    setLoader(loader, isLoading) {
      this.$set(this.loaders, loader, isLoading)
    },

    createAssistanceResolutionsUtils() {
      this.assistanceResolutionsUtils = new AssistanceResolutionsUtils(+new Date(this.dateFrom), +new Date(this.dateTo))
    }
  },

  async mounted() {
    this.createAssistanceResolutionsUtils()

    const routeLogicalBotIds = this.getRouteLogicalBotIds()
    if (routeLogicalBotIds === null) {
      // No explicit `bots` query → fall back to legacy prop (if present) or current global bot.
      await this.retrieveFiltersData(this.botId || botModule.getBotId())
    } else {
      // `bots` query is always logical ids.
      const anchorLogicalBotId = routeLogicalBotIds[0] || botModule.getBotId()
      await this.retrieveFiltersData(anchorLogicalBotId)
    }

    await this.retrieveAnalytics()
  },

  beforeDestroy() {
    cacheModule.resetCaches()
  }
}
</script>
